import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'

import { historyUtils } from '@wartek-id/toolbox-helper'

import {
  EmptyStateAction,
  EmptyStateImage,
  EmptyStateWithIllustration,
} from 'components/EmptyState'
import { LayoutWithHeader as Layout } from 'components/Layout'
import {
  InternalServerError,
  NetworkError,
  ForbiddenError,
} from 'app/ProfileRevampPage/utils/customError'
import AndroidHandler from 'utils/androidHandler'
import { useAuthProvider } from 'utils/auth'
import ImgErrorNoConnection from 'public/images/errors/img-no-connection.svg'

interface ICustomErrorBoundaryProps {
  children: React.ReactNode
  resetKeys: string[]
  onReset: (error: Error) => any
  onError: (error: Error) => any
}
const CustomErrorBoundary = ({
  children,
  resetKeys,
  onReset,
  onError,
}: ICustomErrorBoundaryProps) => {
  return (
    <ErrorBoundary
      resetKeys={resetKeys}
      FallbackComponent={CustomErrorFallback}
      onReset={onReset}
      onError={onError}
    >
      {children}
    </ErrorBoundary>
  )
}

export function CustomErrorFallback({
  error,
  resetErrorBoundary,
}: FallbackProps) {
  const androidHandler = AndroidHandler()
  const router = useRouter()
  const { login, status } = useAuthProvider(false)

  React.useEffect(() => {
    if (status === 'success') {
      resetErrorBoundary()
      window.location.reload()
    }
  }, [status, resetErrorBoundary])

  if (
    error instanceof InternalServerError === false &&
    error instanceof NetworkError === false &&
    error instanceof ForbiddenError === false
  ) {
    throw error
  }

  // @ts-expect-error error is not an Error object
  const { title, description } = error

  return (
    <Layout
      title=""
      onClickBack={() =>
        historyUtils.backToApp(() => {
          resetErrorBoundary()
          router.back()
        })
      }
    >
      <Head>
        <title>{title}</title>
      </Head>
      <EmptyStateWithIllustration
        title={title}
        description={description}
        className="w-full text-center"
      >
        {error instanceof InternalServerError && (
          <>
            <EmptyStateImage
              data-testid="error-boundary-image"
              imageSrc="/images/errors/img-error-fixing.png"
            />
            <EmptyStateAction
              data-testid="error-boundary-action-primary"
              size="lg"
              className="w-full mt-2"
              onClick={() => {
                resetErrorBoundary('InternalServerError')
              }}
            >
              Coba lagi
            </EmptyStateAction>
          </>
        )}

        {error instanceof NetworkError && (
          <>
            <ImgErrorNoConnection
              data-testid="error-boundary-image"
              as="EmptyStateImageSvg"
            />
            <EmptyStateAction
              data-testid="error-boundary-action-primary"
              size="lg"
              className="w-full mt-2"
              onClick={() => {
                resetErrorBoundary('NetworkError')
              }}
            >
              Coba lagi
            </EmptyStateAction>
          </>
        )}

        {error instanceof ForbiddenError && (
          <>
            <EmptyStateImage
              data-testid="error-boundary-image"
              imageSrc="/images/errors/img-no-access.png"
            />
            <EmptyStateAction
              data-testid="error-boundary-action-primary"
              color="black"
              size="lg"
              className="w-full mb-2"
              onClick={() => androidHandler.backToHomepage(login)}
            >
              {androidHandler.enabled
                ? 'Kembali ke Beranda'
                : 'Coba Masuk Lagi'}
            </EmptyStateAction>
            <EmptyStateAction
              data-testid="error-boundary-action-secondary"
              color="white"
              size="lg"
              onClick={() =>
                (window.location.href = `${process.env.NEXT_PUBLIC_PUSAT_INFORMASI_BASE_URL}/hc/id/articles/10078077553817-Tampilan-Error-di-Platform-Merdeka-Mengajar-Versi-Web`)
              }
            >
              Ke Pusat Bantuan
            </EmptyStateAction>
          </>
        )}
      </EmptyStateWithIllustration>
    </Layout>
  )
}

export default CustomErrorBoundary
